import { getContent } from "@lib/use_content";
import { getConfig } from "@lib/use_config";

interface Config {
  id: number;
  creditCard?: {
    cardsNotAccepted?: string[];
  };
  showCcaddress?: boolean;
}

interface Content {
  id: number;
  beCcAddressLabel: string;
  beCcCvvLabel: string;
  beCcExpLabel: string;
  beCcNameLabel: string;
  beCcNumberLabel: string;
  beCcPostalcodeLabel: string;
  beErrCcCvv: string;
  beErrCcExp: string;
  beErrCcAddress: string;
  beErrCcName: string;
  beErrCcNumber: string;
  beErrCcNumberInvalid: string;
  beErrCcPostalcode: string;
  beErrCcNumberType: string;
  beRequiredPlaceholder: string;
}

interface ConfigData {
  config: Config;
}

interface ContentData {
  content: Content;
}

type Dictionary = { [key in EditableField]: DictionaryEntry };

interface DictionaryEntry {
  error?: () => string;
  errorInvalid?: () => string;
  errorType?: () => string;
  label: () => string;
  show: () => boolean;
}

type EditableField = keyof be.CreditCard | "cvv";

export default class CreditCardManager {
  #config: ConfigData | null;
  #content: ContentData | null;

  #dictionary: Dictionary = {
    address: {
      error: () => this.#content?.content.beErrCcAddress || "",
      label: () => this.#content?.content.beCcAddressLabel || "",
      show: () => !!this.#config?.config.showCcaddress,
    },
    cvv: {
      error: () => this.#content?.content.beErrCcCvv || "",
      label: () => this.#content?.content.beCcCvvLabel || "",
      show: () => !!this.#content?.content.beCcCvvLabel,
    },
    expiration: {
      error: () => this.#content?.content.beErrCcExp || "",
      label: () => this.#content?.content.beCcExpLabel || "",
      show: () => true,
    },
    name: {
      error: () => this.#content?.content.beErrCcName || "",
      label: () => this.#content?.content.beCcNameLabel || "",
      show: () => true,
    },
    number: {
      error: () => this.#content?.content.beErrCcNumber || "",
      errorInvalid: () => this.#content?.content.beErrCcNumberInvalid || "",
      errorType: () => this.#content?.content.beErrCcNumberType || "",
      label: () => this.#content?.content.beCcNumberLabel || "",
      show: () => true,
    },
    postalCode: {
      error: () => this.#content?.content.beErrCcPostalcode || "",
      label: () => this.#content?.content.beCcPostalcodeLabel || "",
      show: () => !!this.#content?.content.beCcPostalcodeLabel,
    },
  };

  constructor() {
    this.#config = null;
    this.#content = null;
  }

  async init(): Promise<void> {
    const { data: content, error: cError } = await getContent([
      "beCcAddressLabel",
      "beCcCvvLabel",
      "beCcExpLabel",
      "beCcNameLabel",
      "beCcNumberLabel",
      "beCcPostalcodeLabel",
      "beErrCcAddress",
      "beErrCcCvv",
      "beErrCcExp",
      "beErrCcName",
      "beErrCcNumber",
      "beErrCcNumberInvalid",
      "beErrCcNumberType",
      "beErrCcPostalcode",
      "beRequiredPlaceholder",
    ]);
    const { data: config, error: cfgError } = await getConfig([
      "creditCardCardsNotAccepted",
      "showCcaddress",
    ]);
    if (cError) console.error(cError);
    if (cfgError) console.error(cfgError);
    this.#content = content;
    this.#config = config;
  }

  error(field: EditableField, type: "Invalid" | "Type" | "" = ""): string {
    return this.#dictionary[field][`error${type}`]?.() || "";
  }

  label(field: EditableField): string {
    return this.#dictionary[field].label();
  }

  requiredPlaceholder(): string {
    return this.#content?.content.beRequiredPlaceholder || "";
  }

  show(field: EditableField): boolean {
    return this.#dictionary[field].show();
  }

  invalidCcTypes() {
    return this.#config?.config.creditCardCardsNotAccepted || [];
  }
}
